import { useState } from "react";
import { Box, Button, CircularProgress, Dialog, DialogContent, DialogTitle, IconButton } from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import PreviewOutlinedIcon from "@mui/icons-material/PreviewOutlined";
import { useTranslation } from "react-i18next";
import { useNotify, useRecordContext } from "react-admin";
import { BASE_API_URL } from "../../../config/configUrl";
import { getAccessToken } from "../../../admin/authProvider";

const fetchPreview = async (projectId: string, physicalPayment?: object) => {
  const response = await fetch(`${BASE_API_URL}/projects/${projectId}/payment-page-preview`, {
    method: "POST",
    credentials: "include",
    headers: new Headers({ Authorization: `Bearer ${getAccessToken()}`, "Content-Type": "application/json" }),
    body: JSON.stringify({ physicalPayment }),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || response.statusText);
  }
  return response.text();
};

// Opens the payment page as a payer sees it, for an example order, rendered by the API.
// `draft` carries the form's unsaved settings; without it the saved ones show. The page
// runs without its scripts (sandboxed frame).
const PaymentPagePreviewButton = ({ draft }: { draft?: object }) => {
  const { t }: { t: any } = useTranslation();
  const notify = useNotify();
  const projectId = useRecordContext()?.id;
  const [html, setHtml] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  if (!projectId) return null;

  const open = async () => {
    setLoading(true);
    try {
      setHtml(await fetchPreview(String(projectId), draft));
    } catch (error: any) {
      notify(t("The payment page preview could not be loaded: {{reason}}", { reason: error.message }), { type: "error" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button
        variant="outlined"
        startIcon={loading ? <CircularProgress size={16} /> : <PreviewOutlinedIcon />}
        disabled={loading}
        onClick={open}
      >
        {t("Preview payment page")}
      </Button>
      <Dialog open={html !== null} onClose={() => setHtml(null)} fullWidth maxWidth="lg">
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          {t("Payment page preview")}
          <IconButton aria-label={t("Close")} onClick={() => setHtml(null)}>
            <CloseRoundedIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <Box component="iframe" title={t("Payment page preview")} sandbox="" srcDoc={html ?? ""} sx={{ display: "block", width: "100%", height: "75vh", border: 0 }} />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PaymentPagePreviewButton;
